import { FormEvent, useCallback, useEffect, useState } from 'react';
import { AlertCircle, Loader2, ScrollText, Search, X, LayoutGrid, List, Filter, ChevronUp, ChevronDown, ArrowUpDown } from 'lucide-react';
import { fetchAtosAprovados } from '../services/api/atosAdministrativos';
import type { AtoAprovado } from '../types/atosAdministrativos';
import AtoCard from '../components/atos/AtoCard';
import AtoDetailModal from '../components/atos/AtoDetailModal';
import Pagination from '../components/atos/Pagination';

const ITEMS_PER_PAGE = 12;

export default function AtosAdministrativos() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  
  // Filtros Avançados
  const [showFilters, setShowFilters] = useState(false);
  const [filterNome, setFilterNome] = useState('');
  const [filterConteudo, setFilterConteudo] = useState('');
  const [activeFilterNome, setActiveFilterNome] = useState('');
  const [activeFilterConteudo, setActiveFilterConteudo] = useState('');

  // Visualização e Ordenação
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [sortField, setSortField] = useState<'nome_ato' | 'data_geracao'>('data_geracao');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const [atos, setAtos] = useState<AtoAprovado[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [selectedAto, setSelectedAto] = useState<AtoAprovado | null>(null);

  useEffect(() => {
    setShowDisclaimer(true);
  }, []);

  const loadAtos = useCallback(async (page: number, query: string, fNome: string = '', fConteudo: string = '') => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await fetchAtosAprovados({
        q: query || undefined,
        nome_ato: fNome || undefined,
        conteudo: fConteudo || undefined,
        limit: ITEMS_PER_PAGE,
        offset: page * ITEMS_PER_PAGE,
      });

      setAtos(result.items);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar atos administrativos.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAtos(0, '', '', '');
  }, [loadAtos]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCurrentPage(0);
    setActiveQuery(searchQuery);
    setActiveFilterNome(filterNome);
    setActiveFilterConteudo(filterConteudo);
    loadAtos(0, searchQuery, filterNome, filterConteudo);
  };

  const handleClear = () => {
    setSearchQuery('');
    setActiveQuery('');
    setFilterNome('');
    setFilterConteudo('');
    setActiveFilterNome('');
    setActiveFilterConteudo('');
    setCurrentPage(0);
    loadAtos(0, '', '', '');
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadAtos(page, activeQuery, activeFilterNome, activeFilterConteudo);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSort = (field: 'nome_ato' | 'data_geracao') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedAtos = [...atos].sort((a, b) => {
    let valA, valB;
    if (sortField === 'nome_ato') {
      valA = a.nome_ato.toLowerCase();
      valB = b.nome_ato.toLowerCase();
    } else {
      valA = new Date(a.data_geracao || 0).getTime();
      valB = new Date(b.data_geracao || 0).getTime();
    }
    if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
    if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div className="space-y-6">
      {/* Disclaimer Modal */}
      {showDisclaimer && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-navy-800 border border-navy-200 dark:border-navy-700 animate-in zoom-in-95 duration-300">
            <div className="flex items-center gap-3 text-accent-600 dark:text-accent-400 mb-4">
              <AlertCircle className="h-6 w-6" />
              <h3 className="text-lg font-bold text-navy-900 dark:text-navy-50">Aviso Importante</h3>
            </div>
            <p className="text-navy-600 dark:text-navy-300 leading-relaxed">
              Os modelos aqui disponibilizados são apenas para fins didáticos e acadêmicos, não se responsabilizando o autor pelo seu conteúdo. Não se trata de modelo oficial de nenhum órgão.
            </p>
            <div className="mt-6">
              <button
                onClick={() => setShowDisclaimer(false)}
                className="w-full rounded-xl bg-accent-500 py-3 font-semibold text-white transition-all hover:bg-accent-600 active:scale-[0.98] shadow-lg shadow-accent-500/25"
              >
                Entendi
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div>
        <div className="inline-flex items-center gap-2 rounded-full bg-accent-400/10 px-3 py-1 text-sm font-medium text-accent-600 dark:text-accent-400">
          <ScrollText className="h-4 w-4" />
          Catálogo de Modelos
        </div>
        <h1 className="mt-3 text-2xl font-bold text-navy-900 dark:text-navy-50">
          Modelos de Atos Administrativos
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-navy-500 dark:text-navy-400">
          Consulte modelos prontos de portarias, decretos, resoluções e outros atos administrativos.
          {total > 0 && !isLoading && (
            <span className="ml-1 font-medium text-navy-700 dark:text-navy-200">
              {total} modelos disponíveis.
            </span>
          )}
        </p>
      </div>

      {/* Barra de busca */}
      <div>
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-navy-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por palavra-chave genérica…"
              className="form-input w-full pl-10 pr-10"
              disabled={isLoading}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-navy-400 hover:text-navy-600 dark:hover:text-navy-200"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="inline-flex items-center justify-center rounded-lg px-5 py-2.5 btn-accent disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Search className="h-5 w-5" />
            )}
          </button>
        </form>

        {/* Controles de Filtros e Visualização */}
        <div className="flex flex-col sm:flex-row gap-3 mt-4 justify-between items-center bg-white dark:bg-navy-900 p-3 rounded-lg border border-navy-200 dark:border-navy-700">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${showFilters ? 'bg-accent-100 text-accent-700 dark:bg-accent-900/30 dark:text-accent-400' : 'bg-navy-50 text-navy-600 hover:bg-navy-100 dark:bg-navy-800 dark:text-navy-300 dark:hover:bg-navy-700'}`}
            >
              <Filter className="h-4 w-4" />
              Filtros Avançados
            </button>
          </div>
          <div className="flex gap-1 bg-navy-50 dark:bg-navy-800 p-1 rounded-lg">
            <button
              type="button"
              onClick={() => setViewMode('cards')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'cards' ? 'bg-white shadow-sm text-accent-600 dark:bg-navy-700 dark:text-accent-400' : 'text-navy-500 hover:text-navy-700 dark:text-navy-400 dark:hover:text-navy-200'}`}
              title="Visualização em Cards"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'table' ? 'bg-white shadow-sm text-accent-600 dark:bg-navy-700 dark:text-accent-400' : 'text-navy-500 hover:text-navy-700 dark:text-navy-400 dark:hover:text-navy-200'}`}
              title="Visualização em Lista Detalhada"
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Painel de Filtros Avançados */}
        {showFilters && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-navy-50 dark:bg-navy-800/50 rounded-lg border border-navy-100 dark:border-navy-700/50">
            <div>
              <label className="block text-xs font-medium text-navy-600 dark:text-navy-300 mb-1">Filtrar por Título do Ato</label>
              <input
                type="text"
                value={filterNome}
                onChange={(e) => setFilterNome(e.target.value)}
                placeholder="Ex: Portaria"
                className="form-input w-full text-sm"
                onKeyDown={(e) => e.key === 'Enter' && handleSearch(e as any)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-navy-600 dark:text-navy-300 mb-1">Filtrar por Conteúdo</label>
              <input
                type="text"
                value={filterConteudo}
                onChange={(e) => setFilterConteudo(e.target.value)}
                placeholder="Ex: licitação"
                className="form-input w-full text-sm"
                onKeyDown={(e) => e.key === 'Enter' && handleSearch(e as any)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Erro */}
      {error && (
        <div className="flex items-start gap-3 rounded-lg border border-error-500/30 bg-error-500/10 p-4 text-error-600 dark:text-error-400">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="rounded-xl border border-navy-200 bg-white p-5 dark:border-navy-700 dark:bg-navy-900"
            >
              <div className="skeleton h-5 w-3/4" />
              <div className="mt-3 space-y-2">
                <div className="skeleton h-4 w-full" />
                <div className="skeleton h-4 w-5/6" />
              </div>
              <div className="mt-3 skeleton h-3 w-1/3" />
            </div>
          ))}
        </div>
      )}

      {/* Estado vazio */}
      {!isLoading && !error && atos.length === 0 && (
        <div className="rounded-xl border border-dashed border-navy-300 bg-white/70 p-10 text-center dark:border-navy-700 dark:bg-navy-900/50">
          <ScrollText className="mx-auto h-10 w-10 text-navy-400 dark:text-navy-500" />
          <h2 className="mt-4 text-lg font-semibold text-navy-900 dark:text-navy-100">
            Nenhum ato encontrado
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-navy-500 dark:text-navy-400">
            {activeQuery
              ? `Nenhum resultado para "${activeQuery}". Tente buscar com outros termos.`
              : 'Não foi possível carregar os atos administrativos.'}
          </p>
        </div>
      )}

      {/* Grid de cards ou Tabela */}
      {!isLoading && !error && atos.length > 0 && (
        <>
          {viewMode === 'cards' ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {sortedAtos.map((ato) => (
                <AtoCard
                  key={ato.id}
                  ato={ato}
                  onClick={() => setSelectedAto(ato)}
                />
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-navy-200 dark:border-navy-700 bg-white dark:bg-navy-900 shadow-sm">
              <table className="w-full text-left text-sm text-navy-600 dark:text-navy-300">
                <thead className="bg-navy-50 dark:bg-navy-800/50 text-xs uppercase text-navy-500 dark:text-navy-400 border-b border-navy-200 dark:border-navy-700">
                  <tr>
                    <th className="px-4 py-3 font-medium cursor-pointer hover:bg-navy-100 dark:hover:bg-navy-700 transition-colors" onClick={() => handleSort('nome_ato')}>
                      <div className="flex items-center gap-1">
                        Nome do Ato
                        {sortField === 'nome_ato' ? (sortDirection === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-50" />}
                      </div>
                    </th>
                    <th className="px-4 py-3 font-medium">Descrição</th>
                    <th className="px-4 py-3 font-medium cursor-pointer hover:bg-navy-100 dark:hover:bg-navy-700 transition-colors w-32" onClick={() => handleSort('data_geracao')}>
                      <div className="flex items-center gap-1">
                        Data
                        {sortField === 'data_geracao' ? (sortDirection === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-50" />}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-navy-100 dark:divide-navy-800">
                  {sortedAtos.map((ato) => (
                    <tr key={ato.id} onClick={() => setSelectedAto(ato)} className="hover:bg-navy-50 dark:hover:bg-navy-800/50 cursor-pointer transition-colors">
                      <td className="px-4 py-3 font-medium text-navy-900 dark:text-navy-100">
                        {ato.nome_ato}
                      </td>
                      <td className="px-4 py-3 text-navy-500 dark:text-navy-400 truncate max-w-xs">
                        {ato.descricao}
                      </td>
                      <td className="px-4 py-3 text-navy-500 dark:text-navy-400 whitespace-nowrap">
                        {new Date(ato.data_geracao).toLocaleDateString('pt-BR')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            currentPage={currentPage}
            totalItems={total}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={handlePageChange}
          />
        </>
      )}

      {/* Modal de detalhes */}
      <AtoDetailModal
        ato={selectedAto}
        onClose={() => setSelectedAto(null)}
      />
    </div>
  );
}
